/**
 * OriginalImagesManager.tsx
 *
 * File-level:
 * - Reusable component that manages "Original Media" images for a client.
 * - Allows adding/replacing/deleting user images in development/preview and persists to localStorage.
 * - In production top-level (live) environment, development/preview-only UI (add button and "no images" placeholder)
 *   will not be rendered.
 *
 * Notes:
 * - The "Add Original Images" UI and the "No original media available" placeholder are disabled for the
 *   deployed Edicoo website. They are not rendered for public visitors, even in iframe/previews.
 */

import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Image as ImageIcon, Trash2 } from 'lucide-react'

/**
 * Props for OriginalImagesManager component
 */
export interface OriginalImagesManagerProps {
  /**
   * Client ID used to create a localStorage key so images are saved per-client
   */
  clientId: string
  /**
   * Read-only images that come from the data source (displayed but not editable by the user)
   */
  dataImages?: string[]
  /**
   * Friendly client name used for alt text and titles
   */
  clientName?: string
  /**
   * Optional function to upload a File to a CDN.
   */
  uploadToCdn?: (file: File) => Promise<string>
  /**
   * Show or hide the informational footer text under the grid.
   * - Default: false
   */
  showFooterText?: boolean
}

/**
 * OriginalImagesManager
 *
 * Component-level:
 * - Renders read-only images (from dataImages) and user-added images.
 * - Allows users to add multiple images, replace a specific image, and delete user-added images.
 * - Handles localStorage persistence and optional CDN upload.
 *
 * Important behavior:
 * - The Add buttons and the dashed "no images" placeholder are disabled for the live Edicoo site and are
 *   never rendered, so public users only see existing images when available.
 */
export default function OriginalImagesManager({
  clientId,
  dataImages = [],
  clientName = 'client',
  uploadToCdn,
  showFooterText = false,
}: OriginalImagesManagerProps): JSX.Element | null {
  const storageKey = `client_${clientId}_original_images`
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [originalImages, setOriginalImages] = useState<string[]>([])
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  /**
   * isDev
   *
   * Detect whether the app runs in development mode.
   */
  const isDev =
    (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') ||
    ((typeof import.meta !== 'undefined' ? (import.meta as any).env?.MODE : undefined) === 'development')

  /**
   * isPreview
   *
   * Detect whether the app is running inside an iframe (common for preview editors).
   */
  const isPreview = typeof window !== 'undefined' && window.self !== window.top

  /**
   * showDevControls
   *
   * Final flag that determines whether to render dev-only UI elements.
   * For the deployed site this is forced to false so that no
   * "Add Original Images" buttons or "No original media available"
   * placeholders are ever shown to visitors (including in iframes).
   */
  const showDevControls = false

  /**
   * Load saved original images from localStorage for the given client.
   */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        setOriginalImages(JSON.parse(saved))
      }
    } catch (err) {
      console.error('Failed to load original images from localStorage:', err)
    }
  }, [storageKey])

  /**
   * Persist originalImages array to localStorage.
   */
  const persist = (images: string[]) => {
    setOriginalImages(images)
    try {
      localStorage.setItem(storageKey, JSON.stringify(images))
    } catch (err) {
      console.error('Failed to persist original images:', err)
    }
  }

  /**
   * Create a URL for a file.
   * - Prefer CDN upload if uploadToCdn is provided, otherwise use object URL.
   *
   * @param file File
   * @returns Promise<string> public URL for the image (CDN URL or object URL)
   */
  const createUrlForFile = async (file: File): Promise<string> => {
    if (uploadToCdn) {
      try {
        const cdnUrl = await uploadToCdn(file)
        if (cdnUrl) return cdnUrl
      } catch (err) {
        console.error('CDN upload failed, falling back to object URL:', err)
      }
    }
    return URL.createObjectURL(file)
  }

  /**
   * Handle files selected from the hidden input.
   *
   * @param files FileList | null
   */
  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    setIsUploading(true)
    try {
      const newUrls: string[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('image/')) continue
        // eslint-disable-next-line no-await-in-loop
        const url = await createUrlForFile(file)
        newUrls.push(url)
      }

      if (replaceIndex !== null && replaceIndex >= 0 && replaceIndex < originalImages.length) {
        const updated = originalImages.slice()
        updated[replaceIndex] = newUrls[0] ?? updated[replaceIndex]
        persist(updated)
      } else {
        const updated = [...originalImages, ...newUrls]
        persist(updated)
      }

      setReplaceIndex(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err) {
      console.error('Error processing files:', err)
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Prepare to replace image at `index`. This sets replaceIndex and opens the file picker.
   *
   * @param index number
   */
  const onReplace = (index: number) => {
    setReplaceIndex(index)
    fileInputRef.current?.click()
  }

  /**
   * Remove a user-added original image.
   *
   * @param index number
   */
  const onDelete = (index: number) => {
    const updated = originalImages.filter((_, i) => i !== index)
    persist(updated)
  }

  const hasAnyImage = dataImages.length > 0 || originalImages.length > 0

  /**
   * If there are no images AND we are NOT in development/preview, render nothing.
   * This ensures the "No original media available" placeholder and add-controls are hidden in top-level production.
   */
  if (!hasAnyImage && !showDevControls) {
    return null
  }

  return (
    <div>
      {/* Header: title + Add button (Add button visible only in development OR preview (iframe)) */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-medium text-gray-700">{clientName}</div>
        </div>

        {/* Add button: shown in development OR preview (iframe) */}
        <div>
          <input
            id={`original-image-input-${clientId}`}
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            aria-label="Add or replace original images"
          />
          {showDevControls && (
            <Button
              variant="outline"
              className="bg-transparent"
              onClick={() => {
                fileInputRef.current?.click()
              }}
              disabled={isUploading}
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              {isUploading ? 'Uploading...' : 'Add Original Images'}
            </Button>
          )}
        </div>
      </div>

      {/* Grid of images: readonly data images first, then user-added editable images */}
      {hasAnyImage ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Read-only data images */}
          {dataImages.map((img, i) => (
            <div key={`data-${i}`} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={img}
                alt={`${clientName} image ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/266987bf-c3f8-4c4b-98fa-02f33d6e57a7.png'
                }}
              />
            </div>
          ))}

          {/* User-added editable images */}
          {originalImages.map((img, i) => (
            <div key={`orig-${i}`} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
              <img src={img} alt={`Original image ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-2 flex items-start justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onReplace(i)}
                  className="bg-white bg-opacity-80 text-gray-700 p-1 rounded-md shadow-sm flex items-center space-x-1 text-sm"
                  aria-label="Replace image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v6a4 4 0 004 4h8" />
                  </svg>
                  <span>Replace</span>
                </button>

                <button
                  onClick={() => onDelete(i)}
                  className="bg-red-600 text-white p-1 rounded-md shadow-sm flex items-center space-x-1 text-sm"
                  aria-label="Delete image"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* No images but showDevControls === true (development or preview) -> render dashed placeholder */
        <div className="col-span-full text-center py-8 border-2 border-dashed border-gray-300 rounded-lg mt-4">
          <p className="text-gray-500 mb-4">No original media available</p>
          <div className="flex items-center justify-center space-x-2">
            {showDevControls && (
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="bg-transparent" disabled={isUploading}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Add Original Images
              </Button>
            )}
            <div className="text-sm text-gray-500">PNG / JPG, recommended max 10MB</div>
          </div>
        </div>
      )}

      {/* Footer informational text: conditional by prop */}
      {showFooterText && (
        <div className="mt-4 text-sm text-gray-500">
          Images you add will be shown here and saved in your browser (localStorage) — or uploaded to CDN if configured.
        </div>
      )}
    </div>
  )
}
